import styled from 'styled-components';
import { useState } from 'react';

interface FilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (filters: { 보험종류: string; 사고유형: string }) => void;
}

const FILTER_OPTIONS_BY_TAB: Record<'보험종류' | '사고유형', string[]> = {
  보험종류: ['전체', '실손보험', '자동차보험', '배상책임', '진단금/기타'],
  사고유형: [
    '전체',
    '교통사고',
    '상해사고',
    '후유장해',
    '진단금',
    '누수',
    '도수/체외충격파',
    '백내장',
    '고지의무',
    '전립선',
    '맘모톰',
    '하이푸',
    '비밸브/비중격',
    '무릎치료',
    '어린이',
    '기타',
  ],
};

const FilterModal = ({ isOpen, onClose, onConfirm }: FilterModalProps) => {
  const [activeTab, setActiveTab] = useState<'보험종류' | '사고유형'>('보험종류');
  const [selectedOptions, setSelectedOptions] = useState<{
    보험종류: string;
    사고유형: string;
  }>({ 보험종류: '', 사고유형: '' });

  if (!isOpen) return null;

  const handleOptionClick = (option: string) => {
    setSelectedOptions((prev) => {
      const newOptions = {
        ...prev,
        [activeTab]: prev[activeTab] === option ? '' : option,
      };

      // 보험종류를 선택하면 사고유형을 '전체'로
      if (activeTab === '보험종류' && option !== '전체') {
        newOptions['사고유형'] = '전체';
      }

      // 사고유형을 선택하면 보험종류를 '전체'로
      if (activeTab === '사고유형' && option !== '전체') {
        newOptions['보험종류'] = '전체';
      }

      return newOptions;
    });
  };

  return (
    <>
      <Overlay onClick={onClose} />
      <ModalContainer>
        <Header>
          <TabGroup>
            <Tab selected={activeTab === '보험종류'} onClick={() => setActiveTab('보험종류')}>
              보험종류
            </Tab>
            <Tab selected={activeTab === '사고유형'} onClick={() => setActiveTab('사고유형')}>
              사고유형
            </Tab>
          </TabGroup>
          <CloseButton onClick={onClose}>×</CloseButton>
        </Header>

        <FilterGrid>
          {FILTER_OPTIONS_BY_TAB[activeTab].map((option) => (
            <FilterButton
              key={option}
              selected={selectedOptions[activeTab] === option}
              onClick={() => handleOptionClick(option)}
            >
              {option}
            </FilterButton>
          ))}
        </FilterGrid>

        <ConfirmButton onClick={() => onConfirm(selectedOptions)}>확인</ConfirmButton>
      </ModalContainer>
    </>
  );
};

export default FilterModal;

// 스타일 정의

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 1000;
`;

const ModalContainer = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: #fff;
  border-radius: 20px 20px 0 0;
  padding: 16px;
  z-index: 1001;
  animation: slideUp 0.3s ease-out;

  @keyframes slideUp {
    from {
      transform: translateY(100%);
    }
    to {
      transform: translateY(0%);
    }
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 4px 12px;
  border-bottom: 1px solid #eee;
`;

const TabGroup = styled.div`
  display: flex;
  gap: 12px;
`;

const Tab = styled.div<{ selected: boolean }>`
  cursor: pointer;
  font-family: 'Spoqa Han Sans Neo';
  font-size: 20px;
  font-weight: ${({ selected }) => (selected ? 700 : 400)};
  line-height: 120%;
  letter-spacing: -0.4px;
  color: ${({ selected }) => (selected ? '#191919' : '#6F6F6F')};
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 20px;
  color: #000000;
  cursor: pointer;
`;

const FilterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-top: 16px;
`;

const FilterButton = styled.button<{ selected?: boolean }>`
  padding: 12px 0;
  border-radius: 8px;
  border: none;
  background: ${({ selected }) => (selected ? '#0B46DD' : '#f5f5f5')};
  color: ${({ selected }) => (selected ? '#fff' : '#999')};
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
`;

const ConfirmButton = styled.button`
  margin-top: 24px;
  width: 100%;
  padding: 14px;
  border-radius: 8px;
  background: #0b46dd;
  color: white;
  font-size: 16px;
  font-weight: 600;
  border: none;
  cursor: pointer;
`;
